import { CurrencyPipe } from '@angular/common';
import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { isEmpty, isEqual, size } from 'lodash';
import moment from 'moment-timezone';
import { Subject, Subscription, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { NotificationService } from 'src/core/services/notification.service';

import {
  ACCOUNT_TEMPLATE_TYPES,
  AccountViewModel,
} from '@models/account.class';
import {
  ICashTransactionResponse,
  TRANSACTION_CAPTIONS,
  TRANSACTION_TYPES,
} from '@models/cash-transaction.class';
import { IContract, IContractAccountsCollection } from '@models/contract.class';
import { OperationError } from '@models/operationError.class';
import { AppState } from 'src/app';
import {
  CANCEL_TRANSFERS_STATUS,
  FULL_MODAL_ACTIONS,
} from 'src/app/app.constants';
import { config } from 'src/app/environment-config';
import { FieldSort } from 'src/components/shared/utility/field-sort';
import { SubTransactionType } from 'src/components/trade/transactions/enums/transactionType.enum';
import { BootstrapService, CashService, TradeService } from 'src/core';
import { CustomFormatPipe, OrderByPipe } from 'src/shared/pipes';
const env = config();
const { timeOuts_timeOut30Seg } = env;

@Component({
  selector: 'hb-blotter-cash',
  templateUrl: './blotterCash.component.html',
  styleUrls: ['./blotterCash.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BlotterCashComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showDownloadExcel: boolean;
  @Input() isRefreshing: boolean;
  public showSkeleton = false;
  public ordersEmptyState: any[] = [1, 2, 3];

  bootstrapPromise: Promise<any>;

  monthNames: any[] = [
    'Enero',
    'Febrero',
    'marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  statusTitles: any = {
    1: 'En Reparacion',
    0: 'Pendientes',
    2: 'Detenido',
    64: 'Lista',
    66: 'Detenido',
    10304: 'En cola de envío',
    10688: 'Rechazada',
    11456: 'Exportada',
    14784: 'No Liquidada',
    15296: 'Liquidada',
    63984: 'Cancelada',
    65536: 'Pendientes',
    65538: 'Pendientes',
    65600: 'Lista',
    65602: 'Lista',
    75840: 'En cola de envío',
    76224: 'Rechazada',
    80832: 'Liquidada',
    129520: 'Cancelada',
    '2_': 'Devolución',
    '-1': 'Pendiente',
  };
  statusColors: any = {
    1: 'gray',
    0: 'gray',
    2: 'gray',
    64: 'gray',
    66: 'gray',
    10304: 'gray',
    10688: 'red',
    11456: 'gray',
    14784: 'red',
    15296: 'black',
    63984: 'red',
    65536: 'gray',
    65538: 'gray',
    65600: 'gray',
    65602: 'gray',
    75840: 'gray',
    76224: 'red',
    80832: 'black',
    129520: 'red',
    '2_': 'black',
    '-1': 'gray',
  };

  operationTitles: any = {
    8: 'Depósito',
    9: 'Retiro',
    1: 'Abono por dividendo',
    3590: 'ISR por dividendo',
  };

  dayNames: any[] = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ];

  orders: any[] = [];
  ordersLenght: number;
  totalRows: number;
  currentRows = 0;
  marginTop = 0;
  marginLeft = 0;

  sorting: any = {
    prop: 'processDate',
    reverse: true,
  };
  id = -1;
  width = 0;
  helpHeader =
    'Puedes ver los detalles de SPEI al hacer click sobre el estatus.';
  infoSPEI = '';
  statusName = '';
  trackingKey = '';
  operationDateTime: Date;
  beneficiaryAccount = '';
  operationTime: string;
  operationDate: string;
  operationType = 0;
  additionalInformation = '';
  url = '';
  urlShort = '';
  showTooltipH = false;
  showTooltipR = false;
  public showModalCancelTransfer: boolean;
  public FULL_MODAL_ACTIONS = FULL_MODAL_ACTIONS;
  public transferToCancel: any;
  public parentPosition: any;

  private selectedAccount: any;
  private currentContract: string;
  private speiAccount: any;
  private pageSize = 50;
  private currentPage = 1;
  private sortingProp = 23;
  private sortReverse = false;
  private intervalTimeOut: number = +timeOuts_timeOut30Seg;

  private orderByPipe: OrderByPipe = new OrderByPipe();
  private source: any[] = [];
  private filter = '';
  private blotterFilterSubscription: Subscription;
  private currentContractSubscription: Subscription;
  private currentCashSubscription: Subscription;

  private seekChangeSubscription: Subscription;
  private sort: FieldSort = new FieldSort();
  private isLoremUpdate$: Subject<boolean> = new Subject();
  private isLoremAccount = false;
  private contractAccountsCollection: IContractAccountsCollection;

  constructor(
    private store: Store<AppState>,
    private bootstrapService: BootstrapService,
    private tradeService: TradeService,
    private cashService: CashService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    this.bootstrapPromise = this.bootstrapService.initSession().then(() => {
      this.store
        .select('currentContract')
        .pipe(
          map((currentContract: any) => currentContract.toJS()),
          filter((currentContract: IContract) => !isEmpty(currentContract)),
          distinctUntilChanged((a: IContract, b: IContract) => {
            const isSameContract = a.contractID === b.contractID;
            const isSameLoremAccount = a.isLoremAccount === b.isLoremAccount;
            return isSameContract && isSameLoremAccount;
          }),
          map((currentContract) => {
            const contractChanged: boolean =
              !this.currentContract ||
              this.currentContract !== currentContract.contractID;
            return [currentContract, contractChanged];
          }),
          tap(([currentContract, _contractChanged]: [IContract, boolean]) =>
            this.isLoremUpdate$.next(currentContract.isLoremAccount),
          ),
        )
        .subscribe(
          ([currentContract, contractChanged]: [IContract, boolean]) => {
            const { contractID, isLoremAccount } = currentContract;
            this.selectedAccount = undefined;
            this.currentContract = contractID;
            this.isLoremAccount = isLoremAccount;

            if (contractChanged) {
              this.showSkeleton = true;
              this.orders = [];
              !this.isLoremAccount && this.getSpeiAccount();
              this.id = -1;
            }

            this.isLoremAccount && this.getCashTransactions();
          },
        );
    });
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { isRefreshing } = changes;
    if (isRefreshing && isRefreshing.currentValue !== undefined) {
      if (isRefreshing.currentValue) {
        this.showSkeleton = true;
        if (this.isLoremAccount) {
          this.getCashTransactions();
        } else {
          this.getData();
        }
      } else {
        this.showSkeleton = false;
      }
    }
  }

  ngOnDestroy() {
    [
      this.seekChangeSubscription,
      this.currentContractSubscription,
      this.currentCashSubscription,
      this.blotterFilterSubscription,
    ]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
  }

  onScroll() {
    if (this.showTooltipR === true) {
      const tooltipLeft = document.querySelector('.tooltip-left');
      if (tooltipLeft !== null) {
        (tooltipLeft as HTMLElement).style.visibility = 'hidden';
      }
    }
  }

  getPosition(el: any): any {
    let xPos = 0;
    let yPos = 0;
    let xPosOrder = 0;
    let yPosOrder = 0;

    while (el) {
      if (el.tagName === 'BODY') {
        const xScroll = el.scrollLeft || document.documentElement.scrollLeft;
        const yScroll = el.scrollTop || document.documentElement.scrollTop;

        xPosOrder += el.offsetLeft - xScroll + el.clientLeft;
        yPosOrder += el.offsetTop - yScroll + el.clientTop;
      } else {
        xPos += el.offsetLeft - el.scrollLeft + el.clientLeft;
        yPos += el.offsetTop - el.scrollTop + el.clientTop;
      }

      el = el.offsetParent;
    }
    return {
      x: xPos + xPosOrder,
      y: yPos + yPosOrder,
    };
  }

  viewTooltipH() {
    this.showTooltipH = this.showTooltipH === true ? false : true;
  }

  viewOnlyOneToolTip() {
    if (this.totalRows > 1) {
      this.showTooltipR = false;
    } else {
      this.id = -1;
    }
  }

  showTooltip(status: number): boolean {
    switch (status) {
      case 15296:
      case 80832:
      case 10688:
      case 14784:
      case 63984:
      case 76224:
      case 129520:
      case 999:
        return true;
      default:
        return false;
    }
  }

  showTootltipRow(order: any, id: number) {
    this.showTooltipH = false;
    this.id = id;
    if (this.isLoremAccount) {
      this.generateTooltipLorem(order);
    } else {
      this.generateTooltipLegacy(order);
    }
  }

  fullDate(date: string, dayofweek: any): string {
    if (date !== undefined) {
      const dateElements: any = date.split('/');
      const day: number = parseInt(dateElements[0].toString(), 10);
      const dayConfig: string =
        day.toString().length === 1 ? '0' + day.toString() : day.toString();
      const month: string =
        this.monthNames[parseInt(dateElements[1].toString(), 10) - 1];
      const year: string = dateElements[2].toString();
      return (
        this.dayNames[dayofweek] +
        ' ' +
        dayConfig +
        ' de ' +
        month +
        ' de ' +
        year
      );
    }
  }

  setSortingProp(property: string): void {
    if (!property || property === '') {
      return;
    }

    if (this.sorting.prop === property) {
      this.sorting.reverse = !this.sorting.reverse;
    } else {
      this.sorting.prop = property;
      this.sorting.reverse = true;
    }

    this.sortData();
  }

  generateExcel(): void {
    let generatedFile;
    const date = new Date().getTime();
    const length = this.orders.length;
    const fileHeaders = {
      accountId: 'Contrato',
      date: 'Fecha',
      time: 'Hora',
      folio: 'Folio',
      concept: 'Movimiento',
      amount: 'Monto',
      status: 'Estatus',
    };

    const exportObject = [];
    exportObject.push(fileHeaders);

    for (let i = 0; i < length; i += 1) {
      const transactionData: any = {
        accountId: this.currentContract,
        date: this.orders[i].operationDate,
        time: this.orders[i].operationTime,
        folio: this.orders[i].trackingKey,
        concept: this.orders[i].operationType === 1 ? 'Depósito' : 'Retiro',
        amount: new CurrencyPipe('es-MX').transform(
          this.orders[i].amount,
          'MXN',
          'symbol-narrow',
          '1.2-2',
        ),
        status: this.orders[i].operationStatusName,
      };
      exportObject.push(transactionData);
    }
    generatedFile = new AngularCsv(
      exportObject,
      `GBM_Homebroker_Movimientos_del_dia_${date}`,
    );
  }

  reorderST(property: string): void {
    this.orders = this.sort.SortField(property, this.orders);
  }

  getSpeiAccount() {
    this.cashService
      .getCLABE(this.currentContract)
      .pipe(takeUntil(this.isLoremUpdate$.pipe(filter((isLorem) => isLorem))))
      .subscribe((value: any) => {
        this.speiAccount = value.response;
        this.getData();
      });
  }

  @HostListener('click', ['$event'])
  onClick(e: any) {
    const tooltipLeft = document.querySelector('.tooltip-left');
    this.parentPosition = this.getPosition(e.currentTarget);

    if (e && tooltipLeft !== null) {
      if (this.showTooltipR === true) {
        const xPosition = e.clientX - this.parentPosition.x + 60;
        const yPosition = e.toElement.offsetTop;

        (tooltipLeft as HTMLElement).style.left = xPosition + 'px';
        (tooltipLeft as HTMLElement).style.top = yPosition + 'px';
        (tooltipLeft as HTMLElement).style.visibility = 'visible';
      } else {
        (tooltipLeft as HTMLElement).style.visibility = 'hidden';
      }
    }
  }

  public onShowModalCancelTransfer(tranfer: any) {
    this.showModalCancelTransfer = true;
    this.transferToCancel = tranfer;
  }

  public closeCancelTransferModal(): void {
    this.showModalCancelTransfer = false;
  }

  public cancelTransfer(transfer: any): void {
    this.showModalCancelTransfer = false;
    this.tradeService
      .cancelTransfer(
        this.selectedAccount.contractId,
        this.selectedAccount.accountId,
        transfer.movementDetail.transfer_id,
      )
      .subscribe((cancel: any) => {
        const ammount = transfer.movementDetail.amount.amount;
        const destinationName = transfer.beneficiaryAccount;

        if (cancel.status === CANCEL_TRANSFERS_STATUS.rejected) {
          this.notificationService.showServiceError(
            new OperationError({
              title: 'Cancelación transferencia',
              message: `No se pudo cancelar la transferencia por el monto $${ammount} a la estrategia ${destinationName}`,
              eventId: undefined,
              isBusinessError: false,
            }),
          );
        } else if (cancel.status === CANCEL_TRANSFERS_STATUS.accepted) {
          this.notificationService.showSuccess({
            title: 'Cancelación transferencia',
            message: `Has cancelado la transferencia por el monto $${ammount} a la estrategia ${destinationName}`,
            eventId: undefined,
            isBusinessError: false,
          });
          this.getCashTransactions();
        }
      });
  }

  private filterData(): void {
    this.totalRows = 1;
    this.orders = this.source;
    this.ordersLenght = size(this.orders);
    this.totalRows = this.ordersLenght;
    this.sortData();
  }

  private sortData(): void {
    this.orders = this.orderByPipe.transform(
      this.orders,
      this.getConvertSortingProp(),
    );
  }

  private getData(): void {
    this.totalRows = 1;

    const info: any = {
      contractId: this.currentContract,
      speiAccount: this.speiAccount,
    };

    this.currentCashSubscription = this.tradeService
      .getIntradayCashOperations(info)
      .pipe(takeUntil(this.isLoremUpdate$.pipe(filter((isLorem) => isLorem))))
      .subscribe((data: any) => {
        this.showSkeleton = false;
        let preData: any = [];
        if (data) {
          preData = data;

          this.totalRows = preData.length;

          if (this.totalRows > 0) {
            for (const item of preData) {
              let date: any;
              date = moment(item.operationDate);
              item.operationTime = date.format('hh:mm:ss');
              item.operationDate = date.format('DD/MM/YYYY');
              item.dayOfWeek = moment(date).day();
              item.operationTitle = item.operationType.toString();

              if (
                item.operationType === SubTransactionType.RETREATS &&
                item.operationStatusName !== 'Devolución'
              ) {
                item.amount = -Math.abs(item.amount);
                // item.operationStatus = 15296;
                item.operationStatusName =
                  this.statusTitles[item.operationStatus.toString()];
                item.statusColor =
                  this.statusColors[item.operationStatus.toString()];
              }

              if (item.operationStatusName === 'Devolución') {
                item.operationStatusName = 'Devolución';
                item.statusColor = 'black';
                item.operationStatus = 999;
              }
              item.showTooltip = this.showTooltip(item.operationStatus);
            }

            this.orders = preData;
            this.source = preData;
            this.getTotalOrders(this.orders);
          } else {
            this.source = [];
            this.orders = [];
          }
        } else {
          this.orders = [];
          if (this.totalRows <= 0) {
            this.currentRows = 0;
          }
        }
      });
  }

  private getTotalOrders(orders: any): void {
    this.currentRows = orders.length;
  }

  private getConvertSortingProp(): string {
    return this.sorting.reverse ? `-${this.sorting.prop}` : this.sorting.prop;
  }

  private getCashTransactions() {
    const TRANSACTION_STATUS_PROCESSED = 'processed';
    const TRANSACTION_STATUS_PENDING = 'pending';
    this.store
      .select('contractAccountsCollection')
      .pipe(
        takeUntil(this.isLoremUpdate$),
        map((contractsComMap: any) => contractsComMap.toJS()),
        filter(
          (contractsCom: {
            loaded: boolean;
            loading: boolean;
            data: IContractAccountsCollection;
          }) => contractsCom.loaded,
        ),
        map((collection) => collection.data),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      )
      .pipe(
        switchMap((collection) => {
          this.contractAccountsCollection = collection;
          this.totalRows = 1;
          this.selectedAccount = collection.accounts[this.currentContract];

          const today = moment().format('YYYY-MM-DD');
          const pageNumber = 1;
          const pageSize = 10;
          if (this.selectedAccount) {
            return this.tradeService
              .getMovements(
                this.selectedAccount.contractId,
                this.selectedAccount.accountId,
                pageNumber,
                pageSize,
                '',
                today,
                today,
              )
              .pipe(takeUntil(this.isLoremUpdate$));
          }

          return of({
            items: [] as ICashTransactionResponse[],
          });
        }),
      )
      .subscribe((movements) => {
        const { items } = movements;
        this.totalRows = items.length;
        const transactionsWithTooltip = [
          TRANSACTION_TYPES.withdrawal,
          TRANSACTION_TYPES.transfer_in,
          TRANSACTION_TYPES.transfer_out,
        ];
        this.orders = items.map((item) => {
          const { accountRelation, accounts } = this.contractAccountsCollection;
          const legacyBeneficiaryAccountId =
            accountRelation[item.target_account_id];
          const legacyOriginAccountId = accountRelation[item.source_account_id];
          const beneficiaryAccount = accounts[legacyBeneficiaryAccountId];
          const originAccount = accounts[legacyOriginAccountId];
          const date = moment(item.process_date);
          const updatedItem: any = {
            trackingKey: item.order_id || '--',
            operationDate: date.format('DD/MM/YYYY'),
            operationTime: date.format('hh:mm:ss'),
            dayOfWeek: moment(date).day(),
            operationConcept:
              TRANSACTION_CAPTIONS[item.order_type] || item.order_type,
            amount: item.amount.amount,
            operationStatusName:
              TRANSACTION_CAPTIONS[item.transaction_status] ||
              item.transaction_status,
            showTooltip:
              transactionsWithTooltip.indexOf(
                item.order_type as TRANSACTION_TYPES,
              ) > -1 &&
              (item.transaction_status === TRANSACTION_STATUS_PROCESSED ||
                item.transaction_status === TRANSACTION_STATUS_PENDING),
            statusColor: 'black',
            beneficiaryAccount: beneficiaryAccount
              ? beneficiaryAccount.name
              : '',
            originAccount: originAccount ? originAccount.name : '',
            movementDetail: {
              ...item,
            },
            status: item.transaction_status,
            isCancelable:
              item.transaction_status === TRANSACTION_STATUS_PENDING &&
              item.order_type === TRANSACTION_TYPES.transfer_out,
          };

          return updatedItem;
        });
        this.showSkeleton = false;
      });
  }

  private generateTooltipLegacy(order: any) {
    switch (order.operationStatus) {
      case 15296:
      case 80832:
        this.statusName = order.operationStatusName;
        this.trackingKey = order.trackingKey;
        this.operationDateTime = order.operationDateTime;
        const lastThree = order.beneficiaryAccount.substr(
          order.beneficiaryAccount.length - 3,
        );
        this.beneficiaryAccount = '*****' + lastThree;
        this.operationTime = order.operationTime;
        this.operationDate = this.fullDate(
          order.operationDate.toString(),
          order.dayOfWeek,
        );
        this.url = order.url;
        this.urlShort = order.url.substring(0, 40) + '...';

        this.width = 220;
        this.infoSPEI = `<div>Clave de rastreo:</div>
        <div class="data">${this.trackingKey}</div>
        <div class="title-data">Hora y día de aceptación:</div>
        <div class="data">${this.operationTime}</div>
        <div class="data">${this.operationDate}</div>
        <div class="title-data">Cuenta del cliente beneficiario:</div>
        <div class="data">${this.beneficiaryAccount}</div>
        <div class="title-data">Vínculo:</div>
        <div class="data" ><a href="${this.url}" target="_blank" rel="noopener noreferrer">${this.urlShort}</a></div>`;
        break;
      case 10688:
      case 14784:
      case 63984:
      case 76224:
      case 129520:
      case 999:
        this.additionalInformation = order.additionalInformation;

        this.width = 250;
        this.infoSPEI = `<div class="data">${this.additionalInformation}</div>`;
        break;
      default:
        break;
    }
  }

  private generateTooltipLorem(order: any) {
    const { movementDetail }: { movementDetail: ICashTransactionResponse } =
      order;
    const { accountRelation, accounts } = this.contractAccountsCollection;
    const legacyBeneficiaryAccountId =
      accountRelation[movementDetail.target_account_id];
    const legacyOriginAccountId =
      accountRelation[movementDetail.source_account_id];
    const beneficiaryAccount = accounts[legacyBeneficiaryAccountId];
    const originAccount = accounts[legacyOriginAccountId];

    this.infoSPEI = '';

    switch (movementDetail.order_type) {
      case TRANSACTION_TYPES.withdrawal:
        this.getCashTransactionDetail(order);
        break;

      case TRANSACTION_TYPES.transfer_out:
      case TRANSACTION_TYPES.transfer_in:
        const amount = new CustomFormatPipe().transform(
          order.amount,
          '$0,0.00',
        );
        this.width = 220;
        this.infoSPEI = `
        <div class="data">${order.operationConcept}</div>
        <div class="data">${amount}</div>
        <div class="title-data">${this.getLabelPrefix(
          originAccount,
        )} Origen:</div>
        <div class="data">${order.originAccount}</div>
        <div class="title-data">${this.getLabelPrefix(
          beneficiaryAccount,
        )} Destino:</div>
        <div class="data">${order.beneficiaryAccount}</div>
        <div class="title-data">Fecha</div>
        <div class="data">${order.operationTime}</div>
        <div class="title-data">Hora</div>
        <div class="data">${order.operationDate}</div>
        `;
        break;

      default:
        break;
    }
  }

  private getCashTransactionDetail(order: any) {
    const operationDate = moment(order.operationDate).format('DD-MM-YYYY');
    this.tradeService
      .getCashTransactionDetail(
        this.currentContract,
        operationDate,
        order.movementDetail.order_id,
      )
      .subscribe((detail: any) => {
        if (detail && !!detail[0]) {
          const orderShadowed = detail[0];
          const lastThree = orderShadowed.beneficiary_account.substr(
            orderShadowed.beneficiary_account.length - 3,
          );
          const beneficiaryAccount = '*****' + lastThree;
          const urlShort =
            orderShadowed.banxico_operation_link.substring(0, 40) + '...';
          const processDate = moment(orderShadowed.process_date).format('LLLL');
          this.width = 220;
          this.infoSPEI = `<div>Clave de rastreo:</div>
            <div class="data">${orderShadowed.numeric_reference}</div>
            <div class="title-data">Hora y día de aceptación:</div>
            <div class="data">${processDate}</div>
            <div class="title-data">Cuenta del cliente beneficiario:</div>
            <div class="data">${beneficiaryAccount}</div>
            <div class="title-data">Vínculo:</div>
            <div class="data" ><a href="${orderShadowed.banxico_operation_link}" target="_blank" rel="noopener noreferrer">${urlShort}</a></div>`;
        } else {
          this.viewOnlyOneToolTip();
        }
      });
  }

  private getLabelPrefix(account: AccountViewModel): string {
    return account &&
      account.managementTypeTemplate === ACCOUNT_TEMPLATE_TYPES.SMART_CASH
      ? 'cuenta'
      : 'estrategia';
  }
}
